import React from 'react';
import Grid from '@mui/material/GridLegacy';
import { Box, Typography, Card, CardContent, Chip, Button, FormControl, Select, MenuItem } from '@mui/material';
import { PendingActions, Visibility } from '@mui/icons-material';

function normalizeStatus(s) {
  if (s === 'pending' || s === 'approved' || s === 'rejected') return "didn't_contact_yet";
  return s || "didn't_contact_yet";
}

function statusColor(s) {
  const status = normalizeStatus(s);
  const colors = {
    "didn't_contact_yet": { bg: 'rgba(237, 108, 2, 0.1)', color: 'warning.main' },
    successfully_contacted: { bg: 'rgba(46, 125, 50, 0.1)', color: 'success.main' },
    "didn't_contact_successfully": { bg: 'rgba(255, 152, 0, 0.1)', color: 'warning.main' },
    "didn't_meet_criteria": { bg: 'rgba(156, 39, 176, 0.1)', color: 'secondary.main' },
    rejected_offer: { bg: 'rgba(211, 47, 47, 0.1)', color: 'error.main' },
  };
  return colors[status] || { bg: 'rgba(158, 158, 158, 0.1)', color: 'text.secondary' };
}

export default function BusinessFormsTab({
  businessSubmissions = [],
  onUpdateStatus,
  onViewDetails,
}) {
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 4 }}>
        Business Form Submissions
      </Typography>

      {businessSubmissions.length === 0 ? (
        <Card sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <PendingActions sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No business submissions yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Business form submissions will appear here once users submit them
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {businessSubmissions.map((submission) => {
            const status = normalizeStatus(submission.status);
            const { bg, color } = statusColor(submission.status);
            return (
              <Grid item xs={12} sm={6} md={6} lg={4} key={submission._id}>
                <Card sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {submission.businessName || submission.placeName || 'Business Submission'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {submission.contactName}
                        </Typography>
                      </Box>
                      <Chip
                        label={status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        size="small"
                        sx={{ bgcolor: bg, color, fontWeight: 600 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Email: {submission.email}
                      </Typography>
                      {submission.phone && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Phone: {submission.phone}
                        </Typography>
                      )}
                      {submission.category && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Category: {submission.category}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(43, 233, 123, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)',
                      border: '1px solid rgba(43, 233, 123, 0.2)',
                      mb: 2
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Submitted</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {new Date(submission.submittedAt || submission.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <Select
                          label="Status"
                          value={status}
                          onChange={(e) => onUpdateStatus(submission._id, e.target.value)}
                          sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                          <MenuItem value="didn't_contact_yet">Didn't Contact Yet</MenuItem>
                          <MenuItem value="successfully_contacted">Successfully Contacted</MenuItem>
                          <MenuItem value="didn't_contact_successfully">Didn't Contact Successfully</MenuItem>
                          <MenuItem value="didn't_meet_criteria">Didn't Meet Criteria</MenuItem>
                          <MenuItem value="rejected_offer">Rejected Offer</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => onViewDetails(submission)}
                        fullWidth
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
